import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import authenticationManager from '../Auth/AuthenticationManager';

/**
 * The navigation, described once and rendered two ways.
 *
 * The previous version wrote its links out as markup, which meant the bar and the collapsed mobile
 * list were the same block of JSX shown or hidden with an inline display rule. Describing the items
 * as data instead means the bar and the drawer cannot drift apart, and adding a link is one entry
 * rather than an edit in two places.
 *
 * @returns {Array<{label: string, to?: string, children?: Array<{label: string, to: string}>}>}
 */
function buildNavItems() {
    const isAuthorized = authenticationManager.isUserAuthorized();

    // Only asked once there is a session. These read the stored token, and a signed-out visitor has
    // none - the previous navigation only reached them from inside its signed-in branch.
    const isSuperAdmin = isAuthorized && authenticationManager.isSuperAdmin();
    const isAdmin = isAuthorized && (authenticationManager.isAdmin() || isSuperAdmin);

    const items = [{ label: 'Home', to: '/' }];

    if (isAuthorized) {
        items.push(
            { label: 'Your Account Info', to: '/user-info' },
            { label: 'Change Payment Method', to: '/user-storage-units' },
            {
                label: 'Storage Units',
                children: [
                    { label: 'Your Storage Units', to: '/user-storage-units' },
                    { label: 'Available Storage Units', to: '/available-units' },
                ],
            });

        if (isAdmin) {
            items.push({
                label: 'Admin',
                children: [
                    { label: 'Storage Unit Maintenance', to: '/admin/storage-unit-maintenance' },
                    { label: 'Pending Approvals', to: '/admin/pending-approvals' },
                    { label: 'Current Subscriptions', to: '/admin/current-subscriptions' },
                    { label: 'Storage Unit Holds', to: '/admin/hold-storage-units' },
                    { label: 'Users', to: '/admin/users' },
                    { label: 'Recent Transactions', to: '/admin/payment-log' },
                    { label: 'Approvals Pending Payment', to: '/admin/approvals-pending-payment' },
                    { label: 'Admin Settings', to: '/admin/settings' },
                    ...(isSuperAdmin ? [
                        { label: 'Email Subscribers', to: '/admin/email-subscribers' },
                        { label: 'Text Subscribers', to: '/admin/text-subscribers' },
                        { label: 'Message Log', to: '/admin/message-log' },
                        { label: 'Message Templates', to: '/admin/message-templates' },
                    ] : []),
                ],
            });
        }
    } else {
        items.push(
            { label: 'Available Units', to: '/available-units' },
            { label: 'Register', to: '/register' },
            { label: 'Login', to: '/login' });
    }

    items.push(
        { label: 'Features', to: '/features' },
        {
            label: 'More Info',
            children: [
                { label: 'About Us', to: '/about' },
                { label: 'College Storage', to: '/college' },
            ],
        },
        { label: 'Space Calculator', to: '/calculator' },
        { label: 'Contact Us', to: '/contact' });

    if (isAuthorized) {
        items.push({ label: 'Logout', to: '/logout' });
    }

    return items;
}

export function NavMenu(props) {
    const { isDesktop, isTablet, companyName } = props;

    const navigate = useNavigate();
    const location = useLocation();

    const [drawerOpen, setDrawerOpen] = useState(false);

    // Which dropdown is open, and the button it hangs from.
    const [openMenu, setOpenMenu] = useState(null);

    const items = buildNavItems();
    const showFullBar = isDesktop || isTablet;

    function isCurrent(to) {
        return to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);
    }

    function go(to) {
        setOpenMenu(null);
        setDrawerOpen(false);
        navigate(to);
    }

    const linkStyles = (active) => ({
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        paddingX: '12px',
        borderRadius: 0,
        borderBottom: '3px solid',
        // The current section is marked in the brand colour rather than with a filled background,
        // so the bar still reads as a single surface.
        borderBottomColor: active ? 'primary.light' : 'transparent',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
    });

    return (
        <AppBar
            component={'nav'}
            position={'static'}
            elevation={0}
            sx={{ backgroundColor: '#2c3e50', marginBottom: '20px' }}
        >
            <Toolbar
                sx={showFullBar
                    ? { justifyContent: 'center', minHeight: { xs: '56px', md: '60px' } }
                    : {
                        // Stacked on small screens: the name gets a full-width line of its own so it
                        // cannot be squeezed by the button beside it, and the button sits underneath.
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: '2px',
                        paddingY: '8px',
                    }}
            >
                {showFullBar
                    ? <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {items.map((item) => (item.children
                            ? <Box key={item.label}>
                                <Button
                                    endIcon={<ExpandMoreIcon />}
                                    onClick={(event) => setOpenMenu({
                                        label: item.label,
                                        anchor: event.currentTarget,
                                    })}
                                    sx={linkStyles(item.children.some((child) => isCurrent(child.to)))}
                                >
                                    {item.label}
                                </Button>
                                <Menu
                                    anchorEl={openMenu?.anchor}
                                    open={openMenu?.label === item.label}
                                    onClose={() => setOpenMenu(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                >
                                    {item.children.map((child) => (
                                        <MenuItem
                                            key={child.to}
                                            selected={isCurrent(child.to)}
                                            onClick={() => go(child.to)}
                                        >
                                            {child.label}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                            : <Button
                                key={item.to}
                                component={Link}
                                to={item.to}
                                sx={linkStyles(isCurrent(item.to))}
                            >
                                {item.label}
                            </Button>
                        ))}
                    </Box>
                    : <>
                        <Typography
                            component={Link}
                            to={'/'}
                            sx={{
                                color: '#fff',
                                textDecoration: 'none',
                                fontWeight: 700,
                                fontSize: '1.15rem',
                                lineHeight: 1.3,
                                textAlign: 'center',
                                // Wraps rather than overflowing, for a longer business name.
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {companyName || 'Executive Storage Suites'}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <IconButton
                                edge={'start'}
                                color={'inherit'}
                                aria-label={'Open navigation'}
                                onClick={() => setDrawerOpen(true)}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </>
                }
            </Toolbar>

            {/* A real drawer, rather than a collapsing block with an inline display rule over it. */}
            <Drawer anchor={'left'} open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 280 }} role={'presentation'}>
                    <Typography
                        variant={'subtitle1'}
                        sx={{ fontWeight: 700, padding: '16px', color: 'text.primary' }}
                    >
                        {companyName || 'Executive Storage Suites'}
                    </Typography>
                    <Divider />
                    <List>
                        {items.map((item) => (item.children
                            ? <Box key={item.label}>
                                <Divider sx={{ marginY: '8px' }} />
                                <ListSubheader disableSticky>{item.label}</ListSubheader>
                                {item.children.map((child) => (
                                    <ListItemButton
                                        key={child.to}
                                        selected={isCurrent(child.to)}
                                        onClick={() => go(child.to)}
                                        sx={{ paddingLeft: '32px' }}
                                    >
                                        <ListItemText primary={child.label} />
                                    </ListItemButton>
                                ))}
                            </Box>
                            : <ListItemButton
                                key={item.to}
                                selected={isCurrent(item.to)}
                                onClick={() => go(item.to)}
                            >
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            </Drawer>
        </AppBar>
    );
}

NavMenu.propTypes = {
    /** Shown beside the menu button on small screens, where the site header is hidden. */
    companyName: PropTypes.string,
    isMobile: PropTypes.bool.isRequired,
    isTablet: PropTypes.bool.isRequired,
    isDesktop: PropTypes.bool.isRequired,
};
